import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import CardLayout from "../../components/utils/CardLayout";
import Button from "../../components/utils/Button";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import genericActions from "../../redux/generic/actions";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/utils/Table";
import ToastAlert from "../../components/utils/ToastAlert";
import inventoryTransferActions from "../../redux/inventory/inventoryTransfer/actions";
import { isAdmin } from '../../helpers/commonFunction'

const { inventoryTransferReq, resetMessage } = inventoryTransferActions;
const { getACode } = genericActions;

const MAX_RECORDS = 5000;

const InventoryTransfer = () => {
  const dispatch = useDispatch();
  const [bulkTransfer, setBulkTransfer] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumn, setPreviewColumn] = useState([]);
  const [totalUpload, setTotalUpload] = useState(0);
  const { aCodeList } = useSelector((state) => state.generic);
  const { tokenData } = useSelector((state) => state.auth);
  const [vailidError, setVailidError] = useState("");
  const {
    inventoryTransferMessage,
    inventoryTransferLoading,
    inventoryTransferError,
    inventoryTransferFResult
  } = useSelector((state) => state.inventoryTransfer);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      simType: 26,
      toDealer: isAdmin(tokenData) ? "" : 1,
    },
  });

  useEffect(() => {
    dispatch(getACode());
    return () => {
      dispatch(resetMessage());
    };
  }, []);


  useEffect(() => {
    if (inventoryTransferMessage) {
      ToastAlert({ type: "success", text: inventoryTransferMessage });
      handleReset();
    }
    if (inventoryTransferError) {
      ToastAlert({ type: "error", text: inventoryTransferError });
    }
  }, [inventoryTransferError, inventoryTransferMessage]);


  const handleReset = () => {
    reset();
    setPreviewColumn([]);
    setPreviewData([]);
    setTotalUpload(0);
    setVailidError('')
  };

  const handleDownloadFormat = () => {
    const simpleData = [
      {
        SerialNumber: "1234567890123456789",
        LPA: "LPA:1$T-xxxx.xxx.xxxx-aaaa-bbbbb-cccc",
      },
    ];
    downloadExcelSampleFile(simpleData, "inventory-transfer-format.xlsx");
  };

  const onFileUpload = (e) => {
    const fileReader = new FileReader();
    let file = e.target.files[0];

    setVailidError("");
    setPreviewColumn([]);
    setPreviewData([]);
    setTotalUpload(0);

    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setVailidError("Please upload a valid file (.xlsx, .xls)");
      return;
    }

    fileReader.readAsArrayBuffer(file);

    fileReader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON (rows)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "", // avoid undefined
      });

      // Get columns (headers)
      const headers = Object.keys(jsonData[0] || {});

      const required = ["SerialNumber", "LPA"];
      const isValid =
        headers.length === 2 && required.every((col) => headers.includes(col));

      if (!isValid) {
        setVailidError("Invalid file. required columns: Serial Number and LPA.",);
        return;
      }

      if (jsonData?.length > MAX_RECORDS) {
        setVailidError("Maximum 5000 records allowed.",);
        return;
      }

      const heading = [
        ...headers.map((id) => ({ displayName: `${id}`, key: `${id}` })),
        { displayName: "Remarks", key: "remark" },
        { displayName: "Status", key: "status", textAlign: true },
      ];
      setPreviewColumn(heading);
      const validatedData = validateData(jsonData) || [];

      setPreviewData(validatedData);
      setTotalUpload(validatedData?.length);
    };
  };

  const validateData = (rows) => {
    const serialSeen = new Set();
    const lpaSeen = new Set();

    return rows.map((row) => {
      let remark = [];
      let validFlag = 1;

      const serial = String(row.SerialNumber || "").trim();
      const lpa = String(row.LPA || "").trim();

      // SerialNumber checks
      if (!serial) {
        remark.push("Serial number is missing");
        validFlag = 0;
      } else if (!/^\d+$/.test(serial)) {
        remark.push("Serial number must be numeric");
        validFlag = 0;
      } else if (serial.length !== 19) {
        remark.push("Serial number must be 19 digits");
        validFlag = 0;
      } else if (serialSeen.has(serial)) {
        remark.push("Duplicate serial number");
        validFlag = 0;
      } else {
        serialSeen.add(serial);
      }

      //  LPA checks
      if (!lpa) {
        remark.push("LPA is missing");
        validFlag = 0;
      } else if (lpaSeen.has(lpa)) {
        remark.push("Duplicate LPA");
        validFlag = 0;
      } else {
        lpaSeen.add(lpa);
      }

      return {
        ...row,
        SerialNumber: serial,
        LPA: lpa,
        validFlag,
        remark: remark.length ? remark.join(", ") : "Valid",
      };
    });
  };

  const rowData = useMemo(() => {
    if (previewData && previewData.length > 0) {
      return previewData.map((item) => ({
        ...item,
        status:
          item.validFlag === 1 ? (
            <div className='text-success d-flex align-items-center justify-content-center'>
              <i className="ri-checkbox-circle-fill fs-6"></i> <span className="fw-semibold">Valid</span>
            </div>
          ) : (
            <div className="text-danger d-flex align-items-center justify-content-center">
              <i className="ri-close-circle-fill fs-6"></i><span className="fw-semibold">Invalid</span>
            </div>
          ),
      }));
    }
    return [];
  }, [previewData]);

  const onSubmit = (data) => {
    let simDetails = []
    if (bulkTransfer) {
      simDetails = rowData.map((item) => {
        return {
          simNo: item?.SerialNumber,
          lpa: item?.LPA,
        };
      });
    } else {
      simDetails = [{
        simNo: data?.simNo,
        lpa: data?.lpa,
      }]
    }

    const payload = {
      ...data,
      simDetails: simDetails
    }
    delete payload?.simNo;
    delete payload?.lpa;

    dispatch(inventoryTransferReq(payload));
  };


  const fResultColumn = [
    { displayName: "Serial Number", key: "serialNumber" },
    { displayName: "Remark", key: "remark" },
    { displayName: "Status", key: "status", textAlign: true },
  ]

  const fRowData = useMemo(() => {
    return Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0
      ? inventoryTransferFResult.map((item, key) => {
        return {
          key: key + 1,
          serialNumber: item?.simNo,
          status: item?.responseCode == 0 ? (
            <div className="d-flex align-items-center justify-content-center">
              <span className="text-success fw-semibold d-flex align-items-center">
                <i className="ri-checkbox-circle-fill fs-6"></i>Valid</span>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center">
              <span className="text-danger fw-semibold d-flex align-items-center">
                <i className="ri-close-circle-fill fs-6"></i>
                Invalid
              </span>
            </div>
          ),
          remark: item?.responseMsg,
        };
      })
      : [];
  }, [inventoryTransferFResult]);


  const downloadResponseFile = () => {
    const data = inventoryTransferFResult && inventoryTransferFResult.length > 0 && inventoryTransferFResult.map((item, ind) => (
      {
        "Serial Number": item?.simNo,
        "Remark": item?.responseMsg,
        "Status": item?.responseCode == 0 ? "Valid" : "Invalid"
      }));

    downloadExcelSampleFile(data, "inventory-transfer-response-file.xlsx");
  }

  const handleBack = () => {
    dispatch(resetMessage())
  }


  return (
    <CardLayout
      title={bulkTransfer ? "Bulk Transfer" : "Single Transfer"}
      icon="fas fa-exchange-alt"
      rightContent={
        Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0
          ?
          <div>
            <div className="d-flex gap-2 justify-content-end ">
              <Button
                title="Excel"
                buttonType="button"
                icon={<i className="ri-download-cloud-line me-1" />}
                variant="secondary"
                style={{ padding: "4px 10px", }}
                onClick={downloadResponseFile}
              />
              <Button
                title="Back"
                buttonType="button"
                icon={<i className="ri-arrow-left-line me-1" />}
                variant="primary"
                style={{
                  padding: "4px 10px",
                }}
                onClick={() => handleBack()}
              />
            </div>

          </div> :
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-end gap-2"
          >
            {bulkTransfer && <div className="d-flex align-items-center justify-content-end gap-1 rounded-pill pe-2">
              {
                Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0 ? '' : <button
                  type="button"
                  className="border-0 rounded-2 px-3 py-2"
                  onClick={() => handleDownloadFormat()}
                  style={{ fontSize: 13, fontWeight: 600, background: "#4b9b60", color: "#fff", }}
                >
                  <i className="ri-download-cloud-line" /> Sample File
                </button>
              }
            </div>}
            <div
              className="d-flex align-items-center gap-1 rounded-pill p-1"
              style={{ background: "#f4f2ec", border: "1px solid #e5e2da", }}
            >
              <button
                type="button"
                className="border-0 rounded-pill px-3 py-1"
                onClick={() => { setBulkTransfer(false); handleReset(); }}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  background: !bulkTransfer ? "#8e0f9d" : "transparent",
                  color: !bulkTransfer ? "#fff" : "#6b6b66",
                }}
              >
                Single Transfer
              </button>

              <button
                type="button"
                className="border-0 rounded-pill px-3 py-1"
                onClick={() => { setBulkTransfer(true); handleReset(); }}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  background: bulkTransfer ? "#8e0f9d" : "transparent",
                  color: bulkTransfer ? "#fff" : "#6b6b66",
                }}
              >
                Bulk Transfer
              </button>
            </div>
          </div>
      }
    >
      {
        Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0
          ?
          <div>

          </div> :
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="form-group col-lg-3 col-md-4">
                <label className="form-label fw-semibold">
                  Transfer To <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors?.toDealer ? "is-invalid" : ""}`}
                  {...register("toDealer", {
                    required: "ACode is required"
                  })}
                  disabled={!isAdmin(tokenData)}
                >
                  {isAdmin(tokenData) ?
                    <>
                      <option value="">Select ACode</option>
                      {aCodeList && aCodeList.length > 0 && aCodeList.map((item) => (
                        <option key={item?.Id} value={item?.Id}>
                          {item?.ACode}
                        </option>
                      ))} </> : <option key={1} value={1} disabled> 1001 </option>}

                </select>
                {errors.toDealer && (<div className="invalid-feedback">{errors?.toDealer?.message}</div>)}
              </div>

              {bulkTransfer ? <div className="form-group col-lg-3 col-md-4">
                <label className="form-label fw-semibold">
                  Upload File <span className="text-danger">*</span>
                </label>
                {/* Hidden File Input */}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="form-control"
                  id="fileUpload"
                  onChange={onFileUpload}
                />
                {vailidError && (<div style={{ color: "#dc3545", fontSize: "12px", marginTop: "2px", }} > {vailidError} </div>)}
              </div> : <>
                <div className="form-group col-lg-3 col-md-4">
                  <label className="form-label fw-semibold">
                    Serial Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors?.simNo ? "is-invalid" : ""}`}
                    placeholder="Serial Number"
                    {...register("simNo", {
                      required: "Serial number is required"
                    })}
                  />

                  {errors?.simNo && (
                    <div className="invalid-feedback"> {errors?.simNo?.message} </div>
                  )}
                </div>

                <div className="form-group col-lg-3 col-md-4">
                  <label className="form-label fw-semibold">
                    LPA  <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors?.lpa ? "is-invalid" : ""}`}
                    placeholder="LPA"
                    {...register("lpa", {
                      required: 'LPA is required'
                    })}
                  />
                  {errors?.lpa && (
                    <div className="invalid-feedback">{errors?.lpa?.message}</div>
                  )}
                </div>

              </>}

              <div className="col-lg-3 col-md-6 d-flex align-items-end gap-3" style={{ height: '70px' }}>
                <div className="d-flex flex-wrap gap-2 ">
                  <Button
                    title="Transfer SIM"
                    buttonType="submit"
                    variant="primary"
                    icon={<i className="ri-share-forward-line"></i>}
                    loading={inventoryTransferLoading}
                    disable={inventoryTransferLoading}
                    className="flex-fill"
                  />

                  <Button
                    title="Reset"
                    buttonType="button"
                    variant="secondary"
                    icon={<i className="ri-refresh-line"></i>}
                    onClick={handleReset}
                    className="flex-fill"
                  />

                  {Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0 && (
                    <Button
                      title="Excel"
                      buttonType="button"
                      icon={<i className="ri-download-cloud-line"></i>}
                      variant="secondary"
                      onClick={downloadResponseFile}
                      className="flex-fill"
                    />
                  )}

                </div>
              </div>
            </div>
          </form>
      }

      {Array.isArray(inventoryTransferFResult) && inventoryTransferFResult.length > 0 ? <>
        <div className="mt-3">
          <Table
            columns={fResultColumn}
            loading={false}
            data={fRowData}
            pagination={true}
          />
        </div>
      </> : <>
        {rowData && rowData.length > 0 && (
          <div className="mt-3">
            <Table
              columns={previewColumn}
              loading={false}
              data={rowData}
              pagination={true}
              tableHeight="200px"
            />
          </div>
        )}
      </>}

    </CardLayout>

  );
};

export default InventoryTransfer;
